<script setup>
import { ref, onUnmounted, onMounted } from "vue";

import AppProfileSettings from "./AppProfileSettings.vue";
import AppAssistantSelector from "./AppAssistantSelector.vue";

import { useLayout } from "@/layout/composables/layout";
import { useLayoutStore } from "@/stores/layout";
import { useFlowStore } from "@/stores/flow";

const { onMenuToggle, onProfileSidebarToggle, lg } = useLayout();
const layoutStore = useLayoutStore();
const flowStore = useFlowStore();

const isDesktop = ref(window.innerWidth > lg);
const isEditingTitle = ref(false);
const editableTitle = ref(layoutStore.layout.topbar.title);

function showProfileSidebar() {
  onProfileSidebarToggle();
}

function enableEditingTitle() {
  if (layoutStore.layout.topbar.userIsInFlowScreen) {
    isEditingTitle.value = true;
  }
}

function saveTitle() {
  if (!editableTitle.value) {
    editableTitle.value = layoutStore.layout.topbar.title;
  }
  isEditingTitle.value = false;
  if (layoutStore.layout.topbar.userIsInFlowScreen) {
    editFlowRequestSummary(editableTitle.value);
  }
}

async function editFlowRequestSummary(summary) {
  await flowStore.setFlowRequestSummary(summary);
  layoutStore.setTopbarTitle(summary);
}

const clearTopbarTitle = () => {
  layoutStore.clearTopbarTitle();
};

onMounted(() => {
  window.addEventListener("beforeunload", clearTopbarTitle);
});

onUnmounted(() => {
  window.removeEventListener("beforeunload", clearTopbarTitle);
  clearTopbarTitle();
});

window.addEventListener("resize", () => {
  isDesktop.value = window.innerWidth > lg;
});
</script>

<template>
  <div class="layout-topbar h-10">
    <div class="topbar-start">
      <Button
        v-if="!isDesktop"
        type="button"
        class="topbar-menubutton p-trigger"
        @click="onMenuToggle"
      >
        <i class="pi pi-bars"></i>
      </Button>
      <AppAssistantSelector />
    </div>

    <!-- Editable Title Section -->
    <div v-if="layoutStore.layout.topbar.title" class="topbar-center">
      <p
        v-if="!isEditingTitle && !layoutStore.layout.topbar.userIsInFlowScreen"
        class="topbar-title"
      >
        {{ layoutStore.layout.topbar.title }}
      </p>

      <InputText
        v-if="isEditingTitle && layoutStore.layout.topbar.userIsInFlowScreen"
        v-model="editableTitle"
        class="topbar-title-input"
        @blur="saveTitle"
        @keyup.enter="saveTitle"
      />

      <p v-else class="topbar-title-editable" @click="enableEditingTitle">
        {{ layoutStore.layout.topbar.title }}
      </p>
    </div>
    <div class="topbar-end">
      <ul class="topbar-menu">
        <li></li>
        <li>
          <a
            href="https://docs.neena.io"
            target="_blank"
            rel="noopener noreferrer"
            class="hover:underline"
          >
            Docs
          </a>
        </li>
        <li class="ml-4">
          <Button
            icon="pi pi-cog"
            text
            rounded
            severity="secondary"
            class="!text-color"
            @click="showProfileSidebar"
          ></Button>
        </li>
        <li class="topbar-profile">
          <AppProfileSettings />
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.topbar-title {
  transition: opacity 0.3s ease;
}
</style>
